import React from "react";
import { ImagePlus, Loader2, Save, Upload, X } from "lucide-react";

const fieldClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 outline-none transition focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10";

export default function OrganizationImageModal({
  open,
  preview,
  organization,
  form,
  uploading,
  saving,
  onClose,
  onChange,
  onImageSelect,
  onSave,
}) {
  if (!open) return null;

  const imageUrl = preview || form?.brand_image || organization?.brand_image;
  const disabled = uploading || saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Edit Organization Details
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Fields are synced using editOrganizationInfo.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="mb-5 flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border bg-gray-50">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  className="h-full w-full object-cover"
                  alt="Organization"
                />
              ) : (
                <div className="grid h-full w-full place-items-center text-gray-400">
                  <ImagePlus size={34} />
                </div>
              )}
            </div>

            <div className="w-full flex-1">
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-sm font-semibold text-gray-600 transition hover:border-[#01C37D] hover:bg-emerald-50/40">
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Uploading image...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Choose Brand Image
                  </>
                )}

                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={disabled}
                  onChange={onImageSelect}
                />
              </label>

              <p className="mt-2 text-[10px] leading-4 text-gray-500">
                After upload, click Save Organization to send the image URL with
                the complete organization payload.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                Organization Name
              </label>
              <input
                type="text"
                value={form?.organizationName || ""}
                onChange={(e) => onChange?.("organizationName", e.target.value)}
                placeholder="Organization name"
                className={fieldClass}
                disabled={disabled}
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                GST
              </label>
              <input
                type="text"
                value={form?.gst || ""}
                onChange={(e) =>
                  onChange?.("gst", e.target.value.toUpperCase())
                }
                placeholder="GST number"
                className={fieldClass}
                disabled={disabled}
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                PAN No.
              </label>
              <input
                type="text"
                value={form?.pan_no || ""}
                onChange={(e) =>
                  onChange?.("pan_no", e.target.value.toUpperCase())
                }
                placeholder="PAN number"
                className={fieldClass}
                disabled={disabled}
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                Payment Gateway
              </label>
              <input
                type="text"
                value={form?.pament_gateway || ""}
                onChange={(e) => onChange?.("pament_gateway", e.target.value)}
                placeholder="Payment gateway"
                className={fieldClass}
                disabled={disabled}
              />
              <p className="mt-1 text-[9px] text-gray-400">
                API key is pament_gateway, so the same key is preserved in the
                payload.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                Opening Time
              </label>
              <input
                type="time"
                value={String(form?.opening_time || "").slice(0, 5)}
                onChange={(e) => onChange?.("opening_time", e.target.value)}
                className={fieldClass}
                disabled={disabled}
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                Closing Time
              </label>
              <input
                type="time"
                value={String(form?.closing_time || "").slice(0, 5)}
                onChange={(e) => onChange?.("closing_time", e.target.value)}
                className={fieldClass}
                disabled={disabled}
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                Brand Image URL
              </label>
              <input
                type="text"
                value={form?.brand_image || ""}
                onChange={(e) => onChange?.("brand_image", e.target.value)}
                placeholder="https://..."
                className={fieldClass}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-xs font-bold text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-xl bg-[#01C37D] px-5 py-2.5 text-xs font-bold text-white transition hover:bg-[#00ab6d] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save Organization
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
